import React, { useState, useEffect } from "react";
import { database } from "../../firebase";
import { ref, onValue, remove } from "firebase/database";
import { useAuth } from "../../components/AuthContext";
import { useNavigate, useParams } from "react-router-dom";

import { 
  FaEye, 
  FaCalendarAlt, 
  FaUserCircle, 
  FaPlus, 
  FaTrashAlt, 
  FaChevronLeft, 
  FaChevronRight 
} from "react-icons/fa";

const Board = ({ type: propType }) => {
  const { type: paramType } = useParams();
  const type = propType || paramType;
  const [posts, setPosts] = useState([]);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [boardTypeName, setBoardTypeName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  // 게시판 배경 색상 설정
  const getBoardTheme = () => {
    switch(type) {
      case 'notice':
        return {
          primary: 'bg-blue-600',
          secondary: 'bg-blue-50',
          hover: 'hover:bg-blue-700',
          text: 'text-blue-600',
          border: 'border-blue-300',
          light: 'bg-blue-50'
        };
      case 'faq':
        return {
          primary: 'bg-green-600',
          secondary: 'bg-green-50',
          hover: 'hover:bg-green-700',
          text: 'text-green-600',
          border: 'border-green-300',
          light: 'bg-green-50'
        };
      case 'qna':
        return {
          primary: 'bg-purple-600',
          secondary: 'bg-purple-50',
          hover: 'hover:bg-purple-700',
          text: 'text-purple-600',
          border: 'border-purple-300',
          light: 'bg-purple-50'
        };
      default:
        return {
          primary: 'bg-gray-600',
          secondary: 'bg-gray-50',
          hover: 'hover:bg-gray-700',
          text: 'text-gray-600',
          border: 'border-gray-300',
          light: 'bg-gray-50'
        };
    }
  };

  const theme = getBoardTheme();

  // Firebase에서 데이터 읽기
  useEffect(() => {
    if (!type) {
      navigate("/board/notice");
    }

    setIsLoading(true);

    if (type) {
      const boardTypeRef = ref(database, `boardTypes/${type}`);
      onValue(boardTypeRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setBoardTypeName(data);
        }
      });
    }

    const postsRef = ref(database, `posts/${type}`);

    onValue(postsRef, (snapshot) => {
      const data = snapshot.val();
      const postList = data
        ? Object.entries(data)
            .reverse()
            .map(([id, value]) => ({ id, ...value }))
        : [];
      setPosts(postList);
      setIsLoading(false);
    });
  }, [type, navigate]);

  // 게시글 삭제
  const deletePost = (id) => {
    if (window.confirm("정말로 삭제하시겠습니까?") === true) {
      const postRef = ref(database, `posts/${type}/${id}`);
      remove(postRef).catch((error) => console.error("글 삭제 중 오류:", error));
    }
  };

  // 페이지네이션 계산 로직
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);

  // 페이지 변경 함수
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // 이전/다음 페이지 함수
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // 페이지 범위 계산 (최대 5개 페이지만 표시)
  const getPageRange = () => {
    const totalPageCount = Math.ceil(posts.length / postsPerPage);
    const pageWindowSize = 5;
    let startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    let endPage = startPage + pageWindowSize - 1;
    
    if (endPage > totalPageCount) {
      endPage = totalPageCount;
      startPage = Math.max(1, endPage - pageWindowSize + 1);
    }
    
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  return (
    <section className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 섹션 */}
        <div className="mb-10 bg-white rounded-2xl shadow-md p-6 sm:p-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className={`inline-flex items-center ${theme.secondary} ${theme.text} text-sm font-medium px-3 py-1 rounded-full mb-2`}>
                {boardTypeName}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {boardTypeName} 게시판
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                {boardTypeName}의 최신 소식을 확인하세요.
              </p>
            </div>
            {user && (
              <button
                className={`btn btn-primary ${theme.primary} ${theme.hover} transition-all duration-200 transform hover:scale-105`}
                onClick={() => navigate(`/board/${type}/add`)}
              >
                <FaPlus className="w-4 h-4 mr-2" />
                글쓰기
              </button>
            )}
          </div>
        </div>

        {/* 게시물 리스트 */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
          {isLoading ? (
            <div className="flex justify-center items-center p-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">등록된 게시글이 없습니다.</p>
              <p className="mt-1 text-sm">첫 번째 게시글을 작성해 보세요!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${theme.secondary}`}>
                  <tr>
                    <th scope="col" className="px-3 sm:px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 sm:w-16">
                      번호
                    </th>
                    <th scope="col" className="px-3 sm:px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      제목
                    </th>
                    <th scope="col" className="hidden sm:table-cell px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      작성자
                    </th>
                    <th scope="col" className="hidden sm:table-cell px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      작성일
                    </th>
                    <th scope="col" className="hidden sm:table-cell px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      조회수
                    </th>
                    {isAdmin && (
                      <th scope="col" className="px-3 sm:px-4 py-3.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      관리
                    </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {currentPosts.map((post, index) => (
                    <tr
                      key={post.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        <span className="inline-flex items-center justify-center bg-gray-100 rounded-full h-6 w-6 sm:h-7 sm:w-7">
                          {posts.length - (indexOfFirstPost + index)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-4 min-w-[200px]">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <button
                            onClick={() => navigate(`/board/${type}/${post.id}`)}
                            className={`text-gray-800 ${theme.text} hover:underline font-medium transition-colors duration-200 text-left text-sm sm:text-base break-words w-full`}
                          >
                            {post.title}
                          </button>
                          <div className="flex items-center mt-1 sm:hidden text-xs text-gray-500">
                            <FaUserCircle className="mr-1" />
                            <span>{isAdmin ? "관리자" : post.author.split('@')[0]} · </span>
                            <FaCalendarAlt className="ml-2 mr-1" />
                            <span>{post.createdAt} · </span>
                            <FaEye className="ml-2 mr-1" />
                            <span>{post.views}</span>
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaUserCircle className="mr-2 text-gray-400" />
                          {isAdmin ? "관리자" : post.author.split('@')[0]}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaCalendarAlt className="mr-2 text-gray-400" />
                          {post.createdAt}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaEye className="mr-2 text-gray-400" />
                          {post.views}
                        </div>
                      </td>
                      {(isAdmin) && (
                        <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                          {isAdmin && (
                            <div className="flex justify-center">
                              <button
                                onClick={() => deletePost(post.id)}
                                className="text-red-500 hover:text-red-700 transition-colors duration-200 flex items-center"
                              >
                                <FaTrashAlt className="mr-1" />
                                <span className="hidden sm:inline">삭제</span>
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 페이지네이션 */}
        {posts.length > 0 && (
          <div className="mt-8 flex justify-center">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-2 rounded-l-md border ${theme.border} bg-white text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                } transition-colors duration-150`}
              >
                <FaChevronLeft className="w-4 h-4" />
              </button>
              
              {getPageRange().map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => paginate(pageNumber)}
                  className={`relative inline-flex items-center px-4 py-2 border ${theme.border} text-sm font-medium ${
                    currentPage === pageNumber
                      ? `z-10 ${theme.primary} text-white`
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  } transition-colors duration-150`}
                >
                  {pageNumber}
                </button>
              ))}
              
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-2 rounded-r-md border ${theme.border} bg-white text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                } transition-colors duration-150`}
              >
                <FaChevronRight className="w-4 h-4" />
              </button>
            </nav>
          </div>
        )}
      </div>
    </section>
  );
};

export default Board;